import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HandoffForm } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { TimeSelect } from '@/Domain/components/Shared/TimeSelect/TimeSelect';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { TrashIcon } from '@/Domain/components/icons/Trash.icon';
import { MODAL_NAMES } from '@/Domain/store/modal.store';
import { capitalizeFirstLetter } from '@/Domain/utils/capitalizeFirstLetter/capitalizeFirstLetter';

type Props = {
  form: UseFormReturn<HandoffForm>;
};

const TIME_ZONES = Intl.supportedValuesOf('timeZone');

const WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const workingDaysOptions = [
  { label: 'Every day', value: 'Every day' },
  { label: 'Monday - Friday', value: 'Monday-Friday' },
  ...WEEK_DAYS.map((day) => ({
    label: capitalizeFirstLetter(day),
    value: day,
  })),
];

const timeZonesOptions = TIME_ZONES.map((zone) => ({
  label: zone,
  value: zone,
}));

export const HumanAvailabilityField = ({ form }: Props) => {
  const { t } = useTranslation('handoff_action');
  const { fields, append, remove } = useFieldArray({
    name: 'availability',
    control: form.control,
  });

  const containerNode = document.getElementById(MODAL_NAMES.HANDOFF_ACTION_SHEET);

  const handleAddWorkingHours = () =>
    append({ id: null, day_span: 'Every day', start_time: '09:00', end_time: '17:00' });
  const handleRemoveWorkingHours = (index: number) => remove(index);

  return (
    <div id="human_availability" className="p-4 bg-muted rounded-lg flex flex-col gap-5">
      <header className="flex justify-between">
        <div>
          <FormLabel>{t('form.human_availability_label')}</FormLabel>
          <FormDescription>{t('form.human_availability_description')}</FormDescription>
        </div>

        <FormField
          name="settings.working_hours_active"
          render={({ field }) => (
            <Switch
              defaultChecked={field.value}
              checked={field.value}
              onCheckedChange={(check) => field.onChange(check)}
            />
          )}
        />
      </header>

      <Separator />

      <FormField
        name="settings.timezone"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormLabel>{t('form.timezone_label')}</FormLabel>
            <FormControl>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger size="md" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent containerNode={containerNode}>
                  {timeZonesOptions.map(({ value, label }) => (
                    <SelectItem value={value} key={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />

      <div>
        <FormLabel className="mb-2">{t('form.working_hours_label')}</FormLabel>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className={`mb-4 w-full space-y-2 md:grid md:grid-cols-[1fr_auto_0.25fr_auto_0.25fr_41px] md:items-center md:gap-4 md:space-y-0 ${
              index > 0 ? 'border-t border-border pt-4' : ''
            }`}
          >
            <FormField
              name={`availability.${index}.day_span`}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger size="md" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent containerNode={containerNode}>
                        {workingDaysOptions.map(({ value, label }) => (
                          <SelectItem value={value} key={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            {/* On <md the from/to/times/trash share one row so the day select
                can use the full width above. md:contents makes this wrapper
                transparent to the parent grid on desktop. */}
            <div className="flex w-full flex-wrap items-center gap-2 md:contents">
              <span className="text-sm text-muted-foreground">from</span>
              {/* contents makes each select a cell of the row, so the end-time error can wrap below it. */}
              <FormField
                name={`availability.${index}.start_time`}
                render={({ field }) => (
                  <FormItem className="contents">
                    <FormControl>
                      <TimeSelect {...field} className="min-w-0 flex-1" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <span className="text-sm text-muted-foreground">to</span>
              <FormField
                name={`availability.${index}.end_time`}
                render={({ field }) => (
                  <FormItem className="contents">
                    <FormControl>
                      <TimeSelect
                        {...field}
                        includeEndOfDay
                        className="min-w-0 flex-1"
                        data-testid={`availability-${index}-end-time`}
                      />
                    </FormControl>
                    <FormMessage
                      className="order-last basis-full md:col-span-full"
                      data-testid={`availability-${index}-end-time-error`}
                    />
                  </FormItem>
                )}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    className="h-[41px] w-[41px] shrink-0 self-center !border-destructive/40 text-destructive hover:!border-destructive hover:!bg-destructive-muted hover:!text-destructive"
                    variant="secondary"
                    onClick={() => handleRemoveWorkingHours(index)}
                  >
                    <TrashIcon />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{t('tooltip.delete')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>

      <Button onClick={handleAddWorkingHours} className="w-min" variant="secondary">
        <AddIcon />
        {t('button.add_date')}
      </Button>

      <FormField
        name="settings.confirmation_outside_working_hours_message"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.outside_working_hours_label')}</FormLabel>
            <FormControl>
              <AutosizeTextarea
                {...field}
                minHeight={100}
                className="px-4"
                placeholder={t('form.outside_working_hours_placeholder')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="settings.email_ignores_working_hours"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-4">
            <div>
              <FormLabel>{t('form.email_ignores_working_hours_label')}</FormLabel>
              <FormDescription>{t('form.email_ignores_working_hours_description')}</FormDescription>
            </div>
            <FormControl>
              <Switch
                data-testid="email-ignores-working-hours-switch"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};
